import axios from 'axios';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppDispatch } from '@/core/global-store';
import { setCredentials, setMe } from '@/core/global-store/features/auth/authSlice';
import { endpoints } from '@/lib/axios/axios-global';
import { unwrapSanctumMeResponse, setSession } from '@/features/auth/guard/utils';
import {
    getImpersonationMeta,
    isImpersonating,
    restoreImpersonatorSession,
} from '@/lib/auth/impersonation';

export function ImpersonationBanner() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const active = isImpersonating();
    const meta = getImpersonationMeta();

    const handleExit = useCallback(async () => {
        const meta = getImpersonationMeta();
        const { token, user } = restoreImpersonatorSession();
        if (!token) {
            toast.error('تعذر استعادة جلسة المسؤول.');
            return;
        }

        setSession(token);
        dispatch(setCredentials({ token, user: user as Parameters<typeof setCredentials>[0]['user'] }));

        try {
            const meRes = await axios.get(endpoints.auth.me);
            const mePayload = unwrapSanctumMeResponse(meRes.data);
            dispatch(setMe(mePayload));
            if (mePayload) {
                dispatch(
                    setCredentials({
                        token,
                        user: mePayload as Parameters<typeof setCredentials>[0]['user'],
                    }),
                );
            }
        } catch {
            /* keep restored user */
        }

        toast.success('تم العودة إلى حسابك');
        const returnPath =
            meta?.return_path ??
            (meta?.type === 'tenant' || meta?.tenant_owner_id ? '/subscriptions/tenants' : '/human-resources/employees');
        navigate(returnPath, { replace: true });
    }, [dispatch, navigate]);

    if (!active) return null;

    const isTenant = meta?.type === 'tenant' || Boolean(meta?.tenant_owner_id);
    const targetLabel = isTenant
        ? (meta?.tenant_owner_name ?? (meta?.tenant_owner_id ? `شركة #${meta.tenant_owner_id}` : 'صاحب شركة'))
        : (meta?.employee_name ?? (meta?.employee_id ? `موظف #${meta.employee_id}` : 'موظف'));

    return (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-950">
            <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-2">
                <span>
                    أنت مسجّل الدخول حالياً كـ <strong>{targetLabel}</strong>
                    {meta?.impersonator?.name ? (
                        <span className="text-amber-800/80"> (من حساب {meta.impersonator.name})</span>
                    ) : null}
                </span>
                <button
                    type="button"
                    onClick={() => void handleExit()}
                    className="rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                >
                    العودة لحسابي
                </button>
            </div>
        </div>
    );
}
